import { type AbsolutePath, Path } from "@ostack.tech/kform";
import { useContext, useMemo, useRef } from "react";

import { FormContext } from "../contexts/FormContext.ts";
import { NoFormContextError } from "../utils/errors.ts";

/**
 * Resolves a given path against the current form path or against
 * {@link currentPath} when provided. The returned absolute path has a stable
 * identity.
 *
 * @param path Path to resolve against the current path.
 * @param currentPath Optional current path argument. When provided, this path
 *   is used instead of the form context's current path.
 * @returns Resolved absolute path with a stable identity.
 */
export function useResolvedPath(
  path: Path | string = Path.CURRENT,
  currentPath?: AbsolutePath,
): AbsolutePath {
  const formContext = useContext(FormContext);
  currentPath ??= formContext?.currentPath;
  if (currentPath == null) {
    throw new NoFormContextError();
  }

  const stableAbsolutePathRef = useRef<AbsolutePath>(null);
  const absolutePath = useMemo(
    () => currentPath.resolve(path),
    [currentPath, path],
  );
  if (!absolutePath.equals(stableAbsolutePathRef.current)) {
    stableAbsolutePathRef.current = absolutePath;
  }
  return stableAbsolutePathRef.current!;
}
